import { Fragment, useEffect, useRef, useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import Logo from "./Logo";
import { DEMO_URL, RPO_LINK, SIGN_IN_URL, SITE, navDropdowns } from "./brand";

/**
 * HRMLESS V3 header — the same nav as www.hrmless.com, rebuilt for Zudoku.
 *
 * Structure, states and copy mirror
 * `app/src/components/Navigation.tsx`:
 *   Platform (dropdown) · Industries (full-width mega) · RPO (plain link) ·
 *   Resources (dropdown),
 *   then a "Sign In" outline pill and the blue "Request a demo" pill.
 *   Scroll machine: top (transparent) → hidden (scrolling down) → solid.
 *
 * It is NOT a copy of that component's markup. The public site is Tailwind with
 * the V3 tokens compiled in (`bg-surface-mint`, `font-display`, `rounded-pill`…);
 * none of those classes exist in Zudoku's Tailwind build, so they would silently
 * no-op here. The design tokens arrive instead as the --hl-* custom properties
 * declared in config/theme.tsx, consumed by the scoped CSS at the bottom of this
 * file. When the public nav changes, diff Navigation.tsx against this structure.
 */

type MenuKey = "platform" | "industries" | "resources";

export default function SiteHeader() {
  // V3 navMode machine (Home.dc.html script): 'top' (y<60, transparent),
  // 'hidden' (scrolling down), 'solid' (scrolling up).
  const [navMode, setNavMode] = useState<"top" | "hidden" | "solid">("top");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [active, setActive] = useState<MenuKey | null>(null);

  // Publish the resting (top-state) header height as --hl-nav-h so the blog
  // hero can pull itself under the transparent nav the way the design's hero
  // band does. Measured from the bar rather than the <header>, because the
  // header's own padding shrinks in the solid state and the offset must not.
  const barRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const publish = () => {
      // TOP_PADDING_Y (16 × 2) + the 1px bottom border.
      document.documentElement.style.setProperty("--hl-nav-h", `${el.offsetHeight + 33}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let lastY = 0;
    const onScroll = () => {
      const y = window.scrollY;
      const last = lastY;
      lastY = y;
      setNavMode(y < 60 ? "top" : y > last ? "hidden" : "solid");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Hover-close grace period: the full-width Industries panel anchors to the
  // header bottom, so the pointer crosses a gap between trigger and panel.
  // Without a delay, mouseleave fires mid-gap and unmounts the menu before it
  // can be reached. Entering the panel (a descendant) re-fires mouseenter,
  // which cancels the pending close.
  const closeTimer = useRef<number | null>(null);
  const openMenu = (name: MenuKey) => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setActive(name);
  };
  const scheduleClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setActive(null), 220);
  };

  const linkProps = (external?: boolean) =>
    external ? { target: "_blank", rel: "noopener noreferrer" } : {};

  const hidden = navMode === "hidden" && !mobileOpen && !active;
  const headerClass = [
    "hl-nav",
    navMode === "top" ? "hl-nav--top" : "hl-nav--solid",
    hidden ? "hl-nav--hidden" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <style>{headerCSS}</style>
      <header className={headerClass}>
        <div className="hl-nav__bar" ref={barRef}>
          <a href={SITE} className="hl-nav__logo" aria-label="HRMLESS home">
            <Logo variant="dark" height="30px" />
          </a>

          {/* Desktop nav */}
          <nav className="hl-nav__links">
            {/* Platform — compact dropdown */}
            <div
              className="hl-nav__group"
              onMouseEnter={() => openMenu("platform")}
              onMouseLeave={scheduleClose}
            >
              <button
                className="hl-nav__trigger"
                onClick={() => setActive((a) => (a === "platform" ? null : "platform"))}
                aria-expanded={active === "platform"}
              >
                Platform
                <ChevronDown className={`hl-nav__chev ${active === "platform" ? "is-open" : ""}`} />
              </button>
              {active === "platform" && (
                <div className="hl-nav__panel hl-nav__panel--sm">
                  {navDropdowns.platform.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      {...linkProps(item.external)}
                      className="hl-nav__panel-item"
                      onClick={() => setActive(null)}
                    >
                      <span className="hl-nav__panel-title">{item.name}</span>
                      <span className="hl-nav__panel-desc">{item.desc}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Industries — full-width mega menu */}
            <div
              className="hl-nav__group hl-nav__group--static"
              onMouseEnter={() => openMenu("industries")}
              onMouseLeave={scheduleClose}
            >
              <button
                className="hl-nav__trigger"
                onClick={() => setActive((a) => (a === "industries" ? null : "industries"))}
                aria-expanded={active === "industries"}
              >
                Industries
                <ChevronDown className={`hl-nav__chev ${active === "industries" ? "is-open" : ""}`} />
              </button>
              {active === "industries" && (
                <div className="hl-nav__mega">
                  <div className="hl-nav__mega-inner">
                    <div className="hl-nav__mega-label">Industries</div>
                    <div className="hl-nav__mega-grid">
                      {navDropdowns.industries.map((item) => (
                        <a
                          key={item.href}
                          href={item.href}
                          {...linkProps(item.external)}
                          className="hl-nav__mega-item"
                          onClick={() => setActive(null)}
                        >
                          <span className="hl-nav__mega-title">{item.name}</span>
                          <span className="hl-nav__mega-desc">{item.desc}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* RPO — a plain link, as on the marketing site */}
            <a href={RPO_LINK.href} {...linkProps(RPO_LINK.external)} className="hl-nav__trigger">
              {RPO_LINK.name}
            </a>

            {/* Resources — compact dropdown, pointed back at this site */}
            <div
              className="hl-nav__group"
              onMouseEnter={() => openMenu("resources")}
              onMouseLeave={scheduleClose}
            >
              <button
                className="hl-nav__trigger"
                onClick={() => setActive((a) => (a === "resources" ? null : "resources"))}
                aria-expanded={active === "resources"}
              >
                Resources
                <ChevronDown className={`hl-nav__chev ${active === "resources" ? "is-open" : ""}`} />
              </button>
              {active === "resources" && (
                <div className="hl-nav__panel hl-nav__panel--xs">
                  {navDropdowns.resources.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      {...linkProps(item.external)}
                      className="hl-nav__panel-item"
                      onClick={() => setActive(null)}
                    >
                      <span className="hl-nav__panel-title">{item.name}</span>
                      <span className="hl-nav__panel-desc">{item.desc}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Right actions */}
          <div className="hl-nav__actions">
            <a href={SIGN_IN_URL} target="_blank" rel="noopener noreferrer" className="hl-btn hl-btn--outline">
              Sign In
            </a>
            <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" className="hl-btn hl-btn--blue">
              Request a demo
            </a>
          </div>

          <button
            className="hl-nav__burger"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="hl-nav__burger-icon" /> : <Menu className="hl-nav__burger-icon" />}
          </button>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="hl-nav__drawer">
            {(["platform", "industries", "resources"] as const).map((group) => (
              <Fragment key={group}>
              <div className="hl-nav__drawer-group">
                <div className="hl-nav__drawer-label">{group}</div>
                <div className="hl-nav__drawer-links">
                  {navDropdowns[group].map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      {...linkProps(item.external)}
                      onClick={() => setMobileOpen(false)}
                      className="hl-nav__drawer-link"
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              </div>
              {/* RPO right after Platform, as in the marketing site's mobile drawer. */}
              {group === "platform" && (
                <div className="hl-nav__drawer-group">
                  <a href={RPO_LINK.href} {...linkProps(RPO_LINK.external)} onClick={() => setMobileOpen(false)} className="hl-nav__drawer-link">
                    {RPO_LINK.name}
                  </a>
                </div>
              )}
              </Fragment>
            ))}
            <div className="hl-nav__drawer-actions">
              <a href={SIGN_IN_URL} target="_blank" rel="noopener noreferrer" className="hl-btn hl-btn--outline hl-btn--block">
                Sign In
              </a>
              <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" className="hl-btn hl-btn--blue hl-btn--block">
                Request a demo
              </a>
            </div>
          </div>
        )}
      </header>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Scoped header CSS. Values are the V3 tokens via the --hl-* custom
 * properties declared in config/theme.tsx; the geometry (84px nav band,
 * 1440px container, 44px pill controls, 220ms hover grace) is transcribed
 * from Navigation.tsx's Tailwind classes.
 * ───────────────────────────────────────────────────────────────────────── */
const headerCSS = `
.hl-nav {
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 40;
  border-bottom: 1px solid transparent;
  transition: background-color var(--hl-duration-fast) ease,
              border-color var(--hl-duration-fast) ease,
              box-shadow var(--hl-duration-fast) ease,
              transform var(--hl-duration-fast) ease,
              padding var(--hl-duration-fast) ease;
  font-family: var(--hl-font-ui);
}
.hl-nav * { box-sizing: border-box; }

.hl-nav--top {
  background: transparent;
  border-bottom-color: transparent;
  padding: 16px 0;
}
.hl-nav--solid {
  background: color-mix(in srgb, var(--hl-surface-mint) 95%, transparent);
  backdrop-filter: blur(12px);
  border-bottom-color: var(--hl-border-soft);
  box-shadow: 0 6px 24px rgba(23, 53, 43, 0.08);
  padding: 12px 0;
}
.hl-nav--hidden { transform: translateY(-100%); }

.hl-nav__bar {
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
}

.hl-nav__logo {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  text-decoration: none;
}

/* ── Desktop links ─────────────────────────────────────────────── */
.hl-nav__links {
  display: none;
  align-items: center;
  gap: 32px;
  flex: 1;
  padding-left: 16px;
}
.hl-nav__group { position: relative; }
/* The mega panel spans the header, so its wrapper must not be the containing
   block — the sticky <header> is. */
.hl-nav__group--static { position: static; }

.hl-nav__trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 0;
  background: none;
  border: none;
  cursor: pointer;
  font-family: var(--hl-font-ui);
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  color: var(--hl-text-primary);
  text-decoration: none; /* RPO is an <a> with this class */
  transition: color var(--hl-duration-fast) ease;
}
.hl-nav__trigger:hover { color: var(--hl-green-600); }

.hl-nav__chev {
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
}
.hl-nav__chev.is-open { transform: rotate(180deg); }

/* ── Compact dropdown panels ───────────────────────────────────── */
.hl-nav__panel {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 4px;
  background: var(--hl-surface-page);
  border: 1px solid var(--hl-border-soft);
  border-radius: 10px;
  box-shadow: var(--hl-shadow-menu);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 50;
}
/* Invisible bridge so the pointer can cross from trigger to panel. */
.hl-nav__panel::before,
.hl-nav__mega::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: -12px;
  height: 12px;
}
.hl-nav__panel--sm { width: 420px; }
.hl-nav__panel--xs { width: 340px; }

.hl-nav__panel-item {
  display: block;
  padding: 8px;
  border-radius: 10px;
  text-decoration: none;
  transition: background-color var(--hl-duration-fast) ease;
}
.hl-nav__panel-item:hover { background: var(--hl-surface-mint); }
.hl-nav__panel-title {
  display: block;
  font-family: var(--hl-font-ui);
  font-size: 15px;
  font-weight: 600;
  color: var(--hl-text-primary);
}
.hl-nav__panel-desc {
  display: block;
  margin-top: 2px;
  font-family: var(--hl-font-body);
  font-size: 13px;
  line-height: 1.6;
  color: var(--hl-text-muted);
}

/* ── Full-width Industries mega menu ───────────────────────────── */
.hl-nav__mega {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  background: var(--hl-surface-page);
  border-bottom: 1px solid var(--hl-border-soft);
  box-shadow: var(--hl-shadow-menu);
  z-index: 50;
}
.hl-nav__mega-inner {
  max-width: 1440px;
  margin: 0 auto;
  padding: 40px 24px;
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 40px;
}
.hl-nav__mega-label {
  padding-top: 4px;
  font-family: var(--hl-font-body);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--hl-text-muted);
}
.hl-nav__mega-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 40px;
  row-gap: 28px;
}
.hl-nav__mega-item { text-decoration: none; display: block; }
.hl-nav__mega-title {
  display: block;
  font-family: var(--hl-font-ui);
  font-size: 16px;
  font-weight: 600;
  color: var(--hl-text-primary);
  transition: color var(--hl-duration-fast) ease;
}
.hl-nav__mega-item:hover .hl-nav__mega-title { color: var(--hl-green-600); }
.hl-nav__mega-desc {
  display: block;
  margin-top: 4px;
  font-family: var(--hl-font-body);
  font-size: 15px;
  line-height: 1.6;
  color: var(--hl-text-muted);
}

.hl-nav__actions {
  display: none;
  align-items: center;
  gap: 16px;
}

/* ── Mobile ────────────────────────────────────────────────────── */
.hl-nav__burger {
  display: flex;
  align-items: center;
  padding: 6px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--hl-text-primary);
}
.hl-nav__burger-icon { width: 24px; height: 24px; }

.hl-nav__drawer {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  background: var(--hl-surface-page);
  border-bottom: 1px solid var(--hl-border-soft);
  box-shadow: var(--hl-shadow-menu);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  z-index: 50;
}
.hl-nav__drawer-label {
  margin-bottom: 8px;
  font-family: var(--hl-font-body);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--hl-text-muted);
}
.hl-nav__drawer-links { display: flex; flex-direction: column; gap: 10px; }
.hl-nav__drawer-link {
  padding: 8px;
  border-radius: 8px;
  font-family: var(--hl-font-ui);
  font-size: 15px;
  font-weight: 500;
  color: var(--hl-text-primary);
  text-decoration: none;
}
.hl-nav__drawer-link:hover { background: var(--hl-surface-mint); }
.hl-nav__drawer-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid var(--hl-border-soft);
}

@media (min-width: 1024px) {
  .hl-nav__links { display: flex; }
  .hl-nav__actions { display: flex; }
  .hl-nav__burger { display: none; }
}

/* Container gutter matches the design's px-6 md:px-10. */
@media (min-width: 768px) {
  .hl-nav__bar, .hl-nav__mega-inner { padding-left: 40px; padding-right: 40px; }
}

/* The V3 ultrawide rule: every .dc page widens its containers past 1720px. */
@media (min-width: 1720px) {
  .hl-nav__bar, .hl-nav__mega-inner { max-width: 1800px; }
}
`;
